import { useEffect, useState, MouseEvent } from "react";
import { Box, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useSetRecoilState } from "recoil";
import { BiX } from "react-icons/bi";

import { analytics } from "MVVM/Analytics";
import { useUser } from "MVVM/Hooks/useUser";
import { useTrialCardStatus } from "MVVM/Hooks/useTrialCardStatus";
import { StripePaystationState } from "MVVM/State";
import { TIER_PRICING } from "MVVM/Utilities/SubscriptionTiers";
import {
  getClaimDaysLeft,
  getDaysSinceExpiry,
} from "MVVM/Utilities/TrialCountdown";

const LOCAL_STORAGE_KEY = "trial-reactivation-claim-dismissed";

/**
 * One-shot claim modal for users whose no-card trial expired with the 7 bonus
 * days unclaimed (ALL-1838). Adding a card starts a fresh card-on-file trial
 * week; eligibility and the claim window come from the payment service. The
 * persistent claim states in the account dropdown and Profile Settings carry
 * the offer after this dismisses.
 */
const TrialReactivationUpsell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { allstarUser } = useUser();
  const { status } = useTrialCardStatus();
  const setPaystation = useSetRecoilState(StripePaystationState);

  const eligible = !!status?.reactivationEligible;
  const daysSinceExpiry = getDaysSinceExpiry(status?.reactivationDeadline);
  const claimDaysLeft = getClaimDaysLeft(status?.reactivationDeadline);

  useEffect(() => {
    if (!allstarUser?.loggedIn || !eligible) return;
    if (localStorage.getItem(LOCAL_STORAGE_KEY)) return;
    setIsOpen(true);
    analytics.track("Trial Reactivation - Viewed", {
      location: "Modal",
      daysSinceExpiry,
    });
  }, [allstarUser?.loggedIn, eligible, daysSinceExpiry]);

  if (!isOpen) return <></>;

  const dismiss = () => {
    localStorage.setItem(LOCAL_STORAGE_KEY, "true");
    setIsOpen(false);
    analytics.track("Trial Reactivation - Dismissed", {
      location: "Modal",
      daysSinceExpiry,
    });
  };

  const handleClaim = () => {
    localStorage.setItem(LOCAL_STORAGE_KEY, "true");
    setIsOpen(false);
    analytics.track("Trial Reactivation - Add Card Clicked", {
      location: "Modal",
      daysSinceExpiry,
    });
    setPaystation({
      isModalOpen: true,
      type: "subscribe",
      pendingEmail: false,
      shouldRefreshMtx: false,
      trialReactivation: true,
    });
  };

  return (
    <ModalOverlay onClick={dismiss}>
      <ModalContainer onClick={(e: MouseEvent) => e.stopPropagation()}>
        <CloseButton onClick={dismiss}>
          <BiX size={24} />
        </CloseButton>
        <CardHeader>
          <OfferBadge>Trial ended · 7 bonus days unclaimed</OfferBadge>
          <Title>Your bonus week is still waiting</Title>
        </CardHeader>
        <ModalContent>
          <Blurb>
            Your Unlimited trial ended, but your 7 bonus days didn&apos;t go
            anywhere. Add a card to unlock another week of Unlimited — you
            won&apos;t be charged until it&apos;s up, and you can cancel
            anytime.
          </Blurb>
          <ClaimButton onClick={handleClaim}>
            Add a card — claim 7 more days
          </ClaimButton>
          <SecondaryButton onClick={dismiss}>Maybe later</SecondaryButton>
          <FinePrint>
            Offer expires in {claimDaysLeft}{" "}
            {claimDaysLeft === 1 ? "day" : "days"} · Then{" "}
            {TIER_PRICING.CREATOR_PLAT.monthly}/mo, cancel anytime
          </FinePrint>
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};

const ModalOverlay = styled(Box)`
  align-items: center;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1000;
`;

const ModalContainer = styled(Box)`
  background: ${({ theme }) => theme.colors.ned};
  border: 1px solid ${({ theme }) => theme.colors.tierPlatAlpha40};
  border-radius: 16px;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.25),
    0 2px 4px rgba(0, 0, 0, 0.15),
    0 0 24px ${({ theme }) => theme.colors.tierPlatAlpha20};
  max-width: 480px;
  overflow: hidden;
  position: relative;
  width: 90%;
`;

const CardHeader = styled(Box)`
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.tierPlatAlpha25} 0%,
    transparent 60%
  );
  border-radius: 16px 16px 0 0;
  padding: 28px 24px 20px;
`;

const OfferBadge = styled(Box)`
  background: ${({ theme }) => theme.colors.tierPlatAlpha15};
  border: 1px solid ${({ theme }) => theme.colors.tierPlatAlpha40};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.tierPlat};
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  margin-bottom: 10px;
  padding: 3px 8px;
  text-transform: uppercase;
`;

const CloseButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 8px;
  position: absolute;
  right: 12px;
  top: 12px;
  transition: color 0.2s ease;
  z-index: 2;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const Title = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
`;

const ModalContent = styled(Box)`
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 24px 24px;
`;

const Blurb = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13px;
  line-height: 1.5;
`;

const ClaimButton = styled.button`
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.tierPlat} 0%,
    ${({ theme }) => theme.colors.tierPlatDark} 100%
  );
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 11px 16px;
  transition: filter 0.2s ease;

  &:hover {
    filter: brightness(1.1);
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 9px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.25);
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const FinePrint = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 11.5px;
  margin-top: 2px;
  text-align: center;
`;

export { TrialReactivationUpsell };
